import { Configuration, FrontendApi } from '@ory/client';

import { env } from '@/lib/env/constants';

import { SessionAdapter, type Session } from './adapters';
import { is401Error, is403Error, type ServiceResponse } from './service/types';

/**
 * Ory Kratos API クライアントクラス
 *
 * @example
 * ```ts
 * import { oryKratosClient } from '@/lib/auth/ory/ory-kratos.client';
 *
 * const result = await oryKratosClient.getSession();
 * if (result.data) {
 *   // Session型（拡張された型）を取得
 *   const session = result.data;
 * }
 * ```
 */
export class OryKratosClient {
  private readonly api: FrontendApi;

  /**
   * Ory Kratos API クライアントのインスタンスを作成
   * basePathは環境変数VITE_ORY_KRATOS_URLから取得
   *
   * 403エラーでの再認証処理は、各サービスファイルで`handleOryErrorWithReauth`を使用して処理します。
   */
  constructor() {
    const baseUrl = this.getBasePath();

    // FrontendApiインスタンスを作成
    // 第3引数（axiosInstance）を渡さない場合、デフォルトのインスタンスが使用されます
    this.api = new FrontendApi(
      new Configuration({
        basePath: baseUrl,
        baseOptions: {
          withCredentials: true,
          timeout: 10000,
        },
      }),
    );
  }

  /**
   * 環境変数からベースパスを取得
   */
  private getBasePath(): string {
    const baseUrl = env.VITE_ORY_KRATOS_URL;
    if (!baseUrl) {
      throw new Error('VITE_ORY_KRATOS_URL environment variable is not set');
    }
    return baseUrl;
  }

  /**
   * FrontendApiインスタンスを取得
   *
   * **ログインフロー送信** (`FrontendApi.updateLoginFlow`) は、`transient_payload` を
   * password 送信に付与する必要があるため **`signInService.submitFlow`** 経由のみとすること。
   * ここから `updateLoginFlow` を直接呼ばないこと。
   *
   * @returns FrontendApiインスタンス
   */
  getApi(): FrontendApi {
    return this.api;
  }

  /**
   * 現在のセッションを取得する
   *
   * SessionAdapterを返します。各サービスはこのメソッド経由でセッションを取得します。
   *
   * - ログイン済み: SessionAdapterオブジェクトを返す
   * - 未ログイン (401): null を返す（エラーにはしない）
   * - 403 Forbidden (AALレベル不足など): null を返す
   * - その他エラー: error を返す
   *
   * @param aal - 認証保証レベル（"aal1" | "aal2"）。デフォルトは"aal1"。指定すると、そのAALレベルでセッションを取得しようとします。
   * @returns SessionAdapter。未認証の場合はdataがnull、エラーの場合はerrorが設定される
   *
   * @example
   * ```ts
   * const result = await oryKratosClient.getSession();
   * if (result.error) {
   *   // 予期せぬエラー（500等）
   *   console.error(result.error);
   * } else if (result.data) {
   *   // SessionAdapterを直接使用
   *   const sessionAdapter = result.data;
   *   console.log('Session ID:', sessionAdapter.id);
   *   console.log('Authentication Status:', sessionAdapter.authenticationStatus);
   *   console.log('AAL Level:', sessionAdapter.authenticatorAssuranceLevel);
   * } else {
   *   // 未ログイン（401または403）
   *   console.log('Not authenticated');
   * }
   * ```
   */
  async getSession(
    aal: 'aal1' | 'aal2' = 'aal1',
  ): Promise<ServiceResponse<SessionAdapter>> {
    try {
      type ToSessionRequest = Parameters<typeof this.api.toSession>[0];
      const request: ToSessionRequest = { aal } as ToSessionRequest;
      const { data } = await this.api.toSession(request);

      if (!data) {
        return { data: null, error: null };
      }

      // SessionAdapterを作成して返す
      const sessionAdapter = new SessionAdapter(data as Session);
      return { data: sessionAdapter, error: null };
    } catch (err) {
      // 401 Unauthorized は「未ログイン」という正常な状態
      if (is401Error(err)) {
        return { data: null, error: null };
      }

      // 403 Forbidden (AALレベル不足など) も一旦null扱いにする
      if (is403Error(err)) {
        return { data: null, error: null };
      }

      // ネットワークエラーや500エラーは例外として扱う
      return { data: null, error: err as Error };
    }
  }
}

/**
 * Ory Kratos API クライアントのシングルトンインスタンス
 * 各サービスファイルでこのインスタンスをimportして使用
 */
export const oryKratosClient = new OryKratosClient();
